import React, { useCallback, useState, useMemo, type ReactNode } from 'react';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { Field, ErrorMessage } from '@atlaskit/form';
import Select, { type ValueType } from '@atlaskit/select';
import { CURRENCY_CODES } from '@atlassian/jira-common-constants/src/currency-codes.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import {
	type FormatStyle,
	FORMAT_STYLE_DECIMAL,
	FORMAT_STYLE_PERCENT,
	FORMAT_STYLE_CURRENCY,
} from '../../types.tsx';
import { formatNumberField } from '../../index.tsx';
import type { FormatOptionType, CurrencyOptionType, Props } from './types.tsx';
import messages from './messages.tsx';

// To show the 7 most popular currency options, option height is 32, menu padding is 8
const MENU_HEIGHT = 232;
const FALLBACK_CURRENCY_UNIT = 'USD';
const DEFAULT_PREVIEW_VALUE = 150000;

const currencyOptions: CurrencyOptionType[] = CURRENCY_CODES.map(
	(item: { code: string; symbol: string }) => ({
		label: `${item.symbol} ${item.code}`,
		value: item.code,
	}),
);

export const NumberFieldConfiguration = ({
	onChange,
	defaultConfiguration,
	previewValue,
	showDivider = true,
}: Props) => {
	const { formatMessage } = useIntl();
	const [formatStyle, setFormatStyle] = useState<FormatStyle>(
		defaultConfiguration?.formatStyle ?? FORMAT_STYLE_DECIMAL,
	);
	const [formatUnit, setFormatUnit] = useState<string | null>(
		defaultConfiguration?.formatUnit ?? null,
	);
	const locale = useLocale();

	const formatOptions: FormatOptionType[] = useMemo(
		() => [
			{ label: `# ${formatMessage(messages.numberFormatOption)}`, value: FORMAT_STYLE_DECIMAL },
			{ label: `% ${formatMessage(messages.percentageFormatOption)}`, value: FORMAT_STYLE_PERCENT },
			{ label: `$ ${formatMessage(messages.currencyFormatOption)}`, value: FORMAT_STYLE_CURRENCY },
		],
		[formatMessage],
	);

	const formatStyleDefaultValue = useMemo(
		() =>
			defaultConfiguration?.formatStyle
				? formatOptions.find((option) => option.value === defaultConfiguration.formatStyle)
				: formatOptions[0],
		[defaultConfiguration?.formatStyle, formatOptions],
	);

	const formatUnitDefaultValue = useMemo(() => {
		// Only show defaultConfiguration.formatUnit as the default currency value if the format has not been changed
		return defaultConfiguration?.formatUnit && formatUnit !== FALLBACK_CURRENCY_UNIT
			? currencyOptions.find((option) => option.value === defaultConfiguration.formatUnit)
			: currencyOptions.find((option) => option.value === FALLBACK_CURRENCY_UNIT);
	}, [defaultConfiguration?.formatUnit, formatUnit]);

	const onFormatChange = useCallback(
		(newFormat: FormatOptionType | null) => {
			if (!newFormat || newFormat.value === formatStyle) {
				return;
			}
			setFormatStyle(newFormat.value);
			if (newFormat.value === FORMAT_STYLE_CURRENCY) {
				setFormatUnit(FALLBACK_CURRENCY_UNIT);
				onChange({
					formatStyle: newFormat.value,
					formatUnit: FALLBACK_CURRENCY_UNIT,
					formatDecimals: null,
				});
			} else {
				// No currency unit should be saved if the format is not currency
				setFormatUnit(null);
				onChange({ formatStyle: newFormat.value, formatUnit: null, formatDecimals: null });
			}
		},
		[onChange, formatStyle],
	);

	const onCurrencyChange = useCallback(
		(newCurrency: CurrencyOptionType | null) => {
			if (!newCurrency || newCurrency.value === formatUnit) {
				return;
			}
			setFormatUnit(newCurrency.value);
			onChange({ formatStyle, formatUnit: newCurrency.value, formatDecimals: null });
		},
		[formatStyle, formatUnit, onChange],
	);

	const numberDisplay = useCallback(
		(amount: number) => {
			if (formatUnit) {
				return formatNumberField(amount, { formatStyle, formatUnit, locale });
			}
			return formatNumberField(amount, { formatStyle, locale });
		},
		[formatStyle, formatUnit, locale],
	);

	return (
		<>
			<Field<ValueType<FormatOptionType>>
				name="format"
				label={formatMessage(messages.numberFieldFormatSelectLabel)}
			>
				{({ fieldProps: { id, ...rest }, error }) => (
					<>
						<Select<FormatOptionType>
							inputId={id}
							{...rest}
							testId="platform-utils-number.ui.number-field-configuration.format-select"
							options={formatOptions}
							onChange={onFormatChange}
							defaultValue={formatStyleDefaultValue}
						/>
						{error && <ErrorMessage>{error}</ErrorMessage>}
					</>
				)}
			</Field>
			{formatStyle === FORMAT_STYLE_CURRENCY && (
				<Field<ValueType<CurrencyOptionType>>
					name="currency"
					label={formatMessage(messages.currencyFieldSelectLabel)}
				>
					{({ fieldProps: { id, ...rest }, error }) => (
						<>
							<Select<CurrencyOptionType>
								inputId={id}
								{...rest}
								testId="platform-utils-number.ui.number-field-configuration.currency-select"
								options={currencyOptions}
								onChange={onCurrencyChange}
								defaultValue={formatUnitDefaultValue}
								maxMenuHeight={MENU_HEIGHT}
							/>
							{error && <ErrorMessage>{error}</ErrorMessage>}
						</>
					)}
				</Field>
			)}
			<Box xcss={showDivider ? dividerStyles : gapStyles} />
			<Text testId="platform-utils-number.ui.number-field-configuration.preview-example-text">
				{formatMessage(messages.previewText, {
					preview: numberDisplay(previewValue ?? DEFAULT_PREVIEW_VALUE),
					b: (chunks: ReactNode) => <b>{chunks}</b>,
				})}
			</Text>
		</>
	);
};

const dividerStyles = xcss({
	borderWidth: '1px',
	borderColor: 'color.border',
	borderStyle: 'solid',
	marginBlock: 'space.250',
});

const gapStyles = xcss({
	marginBottom: 'space.200',
});
